export interface CutsceneScene {
  readonly speaker: string;
  readonly speakerDisplayName: string;
  readonly lines: readonly string[];
  readonly mood: string;
}

export interface Cutscene {
  readonly id: string;
  readonly worldId: number;
  readonly position: string;
  readonly scenes: readonly CutsceneScene[];
}

export interface CutsceneCastMember {
  readonly speaker: string;
  readonly displayName: string;
}

export const CUTSCENE_MOODS = [
  'neutral',
  'warm',
  'hopeful',
  'triumphant',
  'tense',
  'danger',
  'dark',
  'mysterious',
] as const;

export type CutsceneMood = (typeof CUTSCENE_MOODS)[number];

export const DEFAULT_MOOD: CutsceneMood = 'neutral';

const KNOWN_MOODS = new Set<string>(CUTSCENE_MOODS);

export class CutsceneParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CutsceneParseError';
  }
}

export function cutsceneFromJson(json: unknown, source = 'cutscene'): Cutscene {
  const record = asRecord(json, source, 'cutscene');
  const id = requiredString(record, 'id', source);
  const scenesValue = record['scenes'];
  if (!Array.isArray(scenesValue) || scenesValue.length === 0) {
    fail(source, '"scenes" must be a non-empty array');
  }

  return {
    id,
    worldId: requiredNumber(record, 'worldId', source),
    position: requiredString(record, 'position', source),
    scenes: scenesValue.map((scene, index) => sceneFromJson(scene, `${source} › scenes[${index}]`)),
  };
}

export function cutsceneSceneFromJson(json: unknown, source = 'scene'): CutsceneScene {
  return sceneFromJson(json, source);
}

export function cutsceneScenesFromJson(json: unknown, source = 'scenes'): CutsceneScene[] {
  if (!Array.isArray(json) || json.length === 0) {
    fail(source, 'expected a non-empty array of scenes');
  }
  return json.map((scene, index) => sceneFromJson(scene, `${source}[${index}]`));
}

export function cutsceneFromScenes(
  id: string,
  worldId: number,
  position: string,
  scenes: readonly CutsceneScene[],
): Cutscene {
  return { id, worldId, position, scenes };
}

export function moodOf(scene: CutsceneScene): CutsceneMood {
  const mood = scene.mood.toLowerCase();
  return KNOWN_MOODS.has(mood) ? (mood as CutsceneMood) : DEFAULT_MOOD;
}

export function cutsceneCast(cutscene: Cutscene): readonly CutsceneCastMember[] {
  const cast: CutsceneCastMember[] = [];
  const seen = new Set<string>();

  for (const scene of cutscene.scenes) {
    if (seen.has(scene.speaker)) continue;
    seen.add(scene.speaker);
    cast.push({ speaker: scene.speaker, displayName: scene.speakerDisplayName });
  }
  return cast;
}

export function cutsceneLineCount(cutscene: Cutscene): number {
  return cutscene.scenes.reduce((total, scene) => total + scene.lines.length, 0);
}

function sceneFromJson(json: unknown, source: string): CutsceneScene {
  const record = asRecord(json, source, 'scene');
  const linesValue = record['lines'];

  if (!Array.isArray(linesValue) || linesValue.length === 0) {
    fail(source, '"lines" must be a non-empty array');
  }
  linesValue.forEach((line, index) => {
    if (typeof line !== 'string' || line.trim() === '') {
      fail(source, `"lines[${index}]" must be a non-empty string`);
    }
  });

  const mood = record['mood'];
  if (mood !== undefined && typeof mood !== 'string') {
    fail(source, '"mood" must be a string when present');
  }

  return {
    speaker: requiredString(record, 'speaker', source),
    speakerDisplayName: requiredString(record, 'speakerDisplayName', source),
    lines: linesValue as string[],
    mood: mood ?? DEFAULT_MOOD,
  };
}

function fail(source: string, detail: string): never {
  throw new CutsceneParseError(`${source}: ${detail}`);
}

function asRecord(value: unknown, source: string, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail(source, `${what} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requiredString(record: Record<string, unknown>, key: string, source: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim() === '') {
    fail(source, `"${key}" must be a non-empty string`);
  }
  return value;
}

function requiredNumber(record: Record<string, unknown>, key: string, source: string): number {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(source, `"${key}" must be a number`);
  }
  return value;
}
